import React, { useEffect, useState, useMemo } from 'react';
import {
  fetchHiringRequests,
  GetDemandPlanning,
  fetchActiveDepartments,
  GetCandidate,
  fetchHiringManagers,
  fetchNotifications,
} from '../services/apiService';
import { getDepartmentIdFromToken, getToken, getUserFromToken } from '../services/authService';
import { useNavigate } from 'react-router-dom';
import CandidateCardsList from '../components/CandidateCardsList';
import HiringRequestCards from '../components/HiringRequestCards';
import FlipCard from '../components/FlipCard';
import CreateHiringRequestModal from '../components/CreateHiringRequestModal';
import toast from 'react-hot-toast';
import Select from 'react-select';
import LoadingSpinner from '../components/LoadingSpinner';
import {
  AreaChart, Area,
  BarChart, Bar,
  PieChart, Pie, Cell,
  LineChart, Line,
  XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer,
} from 'recharts';

// ─── Chart Colors ─────────────────────────────────────────────────────────────
const CHART_COLORS = [
  'rgb(18,76,114)',
  'rgba(18,76,114,0.75)',
  'rgba(18,76,114,0.52)',
  'rgba(18,76,114,0.35)',
  'rgba(16,185,129,0.85)',
  'rgba(59,130,246,0.85)',
  'rgba(245,158,11,0.85)',
  'rgba(239,68,68,0.85)',
  'rgba(168,85,247,0.85)',
];

// ─── Status color map ─────────────────────────────────────────────────────────
const STATUS_COLORS = {
  Draft:             'rgba(245,158,11,0.85)',
  Submitted:         'rgba(168,85,247,0.85)',
  Approved:          'rgba(16,185,129,0.85)',
  Rejected:          'rgba(239,68,68,0.85)',
  'On Hold':         'rgba(107,114,128,0.85)',
  Sourcing:          'rgba(59,130,246,0.85)',
  Shortlisting:      'rgba(14,165,233,0.85)',
  Shortlisted:       'rgba(6,182,212,0.85)',
  Interviewing:      'rgba(99,102,241,0.85)',
  'Hiring Approvals':'rgba(34,197,94,0.85)',
  Offering:          'rgba(249,115,22,0.85)',
  Joining:           'rgba(20,184,166,0.85)',
  Joined:            'rgb(18,76,114)',
};

// ─── Select Styles ────────────────────────────────────────────────────────────
const selectStyles = {
  control: (p, s) => ({
    ...p,
    borderRadius: '0.75rem',
    borderColor: s.isFocused ? 'rgb(18,76,114)' : 'rgba(18,76,114,0.25)',
    boxShadow: s.isFocused ? '0 0 0 3px rgba(18,76,114,0.12)' : 'none',
    padding: '1px 6px', minHeight: '38px',
    '&:hover': { borderColor: 'rgb(18,76,114)' },
  }),
  menu: (p) => ({ ...p, borderRadius: '0.75rem', boxShadow: '0 10px 25px rgba(18,76,114,0.15)', zIndex: 1000 }),
  option: (p, s) => ({
    ...p,
    backgroundColor: s.isSelected ? 'rgba(18,76,114,0.1)' : s.isFocused ? 'rgba(18,76,114,0.05)' : 'white',
    color: 'rgb(18,76,114)', cursor: 'pointer', padding: '8px 12px',
  }),
  singleValue: (p) => ({ ...p, color: 'rgb(18,76,114)', fontWeight: '600' }),
  dropdownIndicator: (p) => ({ ...p, color: 'rgba(18,76,114,0.5)', ':hover': { color: 'rgb(18,76,114)' } }),
  indicatorSeparator: () => ({ display: 'none' }),
  placeholder: (p) => ({ ...p, color: 'rgba(18,76,114,0.4)', fontSize: '14px' }),
};

// ─── Shared Sub-Components ────────────────────────────────────────────────────
const Icon = ({ path, size = 5 }) => (
  <svg className={`w-${size} h-${size}`} style={{ color: 'rgb(18,76,114)' }}
    fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path} />
  </svg>
);

const TabBtn = ({ active, onClick, children, count }) => (
  <button onClick={onClick}
    className="px-4 py-2 text-sm font-medium rounded-lg transition-colors whitespace-nowrap"
    style={{
      backgroundColor: active ? 'rgb(18,76,114)' : 'rgba(18,76,114,0.05)',
      color: active ? 'white' : 'rgba(18,76,114,0.7)',
      border: active ? 'none' : '1px solid rgba(18,76,114,0.1)',
    }}>
    {children} <span className="ml-1 opacity-80">({count})</span>
  </button>
);

const KpiCard = ({ label, count, iconPath, onClick, highlight, sub }) => (
  <div onClick={onClick}
    className="p-5 rounded-xl cursor-pointer transition-all hover:shadow-md active:scale-95"
    style={{
      backgroundColor: highlight ? 'rgba(18,76,114,0.05)' : 'white',
      border: `${highlight ? 2 : 1}px solid rgba(18,76,114,${highlight ? 0.3 : 0.1})`,
      boxShadow: '0 4px 12px rgba(18,76,114,0.06)',
    }}>
    <div className="flex items-center justify-between mb-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center"
        style={{ backgroundColor: 'rgba(18,76,114,0.08)' }}>
        <Icon path={iconPath} />
      </div>
      <span className="text-3xl font-bold" style={{ color: 'rgb(18,76,114)' }}>{count}</span>
    </div>
    <p className="text-sm font-medium" style={{ color: 'rgba(18,76,114,0.7)' }}>{label}</p>
    {sub && <p className="text-xs mt-0.5" style={{ color: 'rgba(18,76,114,0.45)' }}>{sub}</p>}
  </div>
);

const ChartCard = ({ title, subtitle, children, colSpan = '' }) => (
  <div className={`${colSpan} p-5 rounded-xl`}
    style={{ border: '1px solid rgba(18,76,114,0.1)', backgroundColor: 'rgba(18,76,114,0.015)' }}>
    <div className="mb-4">
      <h4 className="font-semibold text-sm" style={{ color: 'rgb(18,76,114)' }}>{title}</h4>
      {subtitle && <p className="text-xs mt-0.5" style={{ color: 'rgba(18,76,114,0.5)' }}>{subtitle}</p>}
    </div>
    {children}
  </div>
);

const EmptyChart = ({ h = 200 }) => (
  <div className="flex flex-col items-center justify-center" style={{ height: h, color: 'rgba(18,76,114,0.25)' }}>
    <svg className="w-8 h-8 mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
        d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
    <p className="text-xs">No data available</p>
  </div>
);

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="px-4 py-3 rounded-xl shadow-lg"
      style={{ backgroundColor: 'white', border: '1px solid rgba(18,76,114,0.15)', minWidth: 130 }}>
      {label && <p className="text-xs font-semibold mb-2" style={{ color: 'rgba(18,76,114,0.55)' }}>{label}</p>}
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full" style={{ backgroundColor: p.color || p.fill }} />
          <p className="text-sm font-semibold" style={{ color: 'rgb(18,76,114)' }}>
            {p.name}: <span style={{ color: p.color || p.fill }}>{p.value}</span>
          </p>
        </div>
      ))}
    </div>
  );
};

const legendFormatter = (val) => (
  <span style={{ fontSize: 11, color: 'rgba(18,76,114,0.65)' }}>{val}</span>
);

// ═════════════════════════════════════════════════════════════════════════════
const AdminDashboard = () => {
  const navigate = useNavigate();
  const userRole   = getToken();
  const userName   = getUserFromToken();
  const departmentId  = getDepartmentIdFromToken();
  const isDeptRestricted = userRole?.IsDepartmentRestricted === 'True';

  // ── State ────────────────────────────────────────────────────────────────
  const [loading, setLoading]           = useState(true);
  const [activeTab, setActiveTab]       = useState('overview');
  const [showCreateModal, setShowCreateModal] = useState(false);

  const [hiringRequests, setHiringRequests]   = useState([]);
  const [demandPlans, setDemandPlans]         = useState([]);
  const [departments, setDepartments]         = useState([]);
  const [hiringManagers, setHiringManagers]   = useState([]);
  const [candidates, setCandidates]           = useState([]);
  const [notifications, setNotifications]     = useState([]);

  // Request tab filters
  const [deptFilter, setDeptFilter]     = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm]     = useState('');
  const [visibleCount]                  = useState(50);

  // ── Load data ─────────────────────────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      try {
        const currentYear = new Date().getFullYear();
        const [reqData, planData, deptData, managers, candidateData, notifData] = await Promise.all([
          fetchHiringRequests(),
          GetDemandPlanning(currentYear),
          fetchActiveDepartments(),
          fetchHiringManagers(),
          GetCandidate(),
          fetchNotifications(userRole?.role, userName),
        ]);

        setHiringManagers(managers);

        const byDept = (arr, key = 'department_id') =>
          isDeptRestricted ? arr.filter(i => String(i[key]) === String(departmentId)) : arr;

        setHiringRequests(byDept(reqData));
        setDemandPlans(byDept(planData));
        setDepartments(isDeptRestricted
          ? deptData.filter(d => String(d.department_id) === String(departmentId))
          : deptData);
        setNotifications(byDept(notifData));

        let cands = [...candidateData].sort(
          (a, b) => new Date(b.application_date) - new Date(a.application_date)
        );
        if (isDeptRestricted) cands = cands.filter(c => String(c.department_id) === String(departmentId));
        setCandidates(cands);
      } catch (err) {
        console.error(err);
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  // ── KPIs ──────────────────────────────────────────────────────────────────
  const kpis = useMemo(() => ({
    total:      hiringRequests.length,
    pending:    hiringRequests.filter(r => r.status === 'Submitted').length,
    approved:   hiringRequests.filter(r => r.status === 'Approved').length,
    sourcing:   hiringRequests.filter(r => r.isConevrtToSource === true).length,
    rejected:   hiringRequests.filter(r => r.status === 'Rejected').length,
    draft:      hiringRequests.filter(r => r.status === 'Draft').length,
    onHold:     hiringRequests.filter(r => r.status === 'On Hold').length,
    candidates: candidates.length,
    demandPlans:demandPlans.length,
    forecasted: demandPlans.reduce((s, p) => s + (Number(p.forecasted_hires) || 0), 0),
    depts:      departments.length,
  }), [hiringRequests, candidates, demandPlans, departments]);

  // ── Filtered requests (tab) ───────────────────────────────────────────────
  const filteredRequests = useMemo(() => {
    return hiringRequests.filter(r => {
      const dMatch = deptFilter === 'all' || String(r.department_id) === String(deptFilter);
      const sMatch = statusFilter === 'all' || r.status === statusFilter;
      const q = searchTerm.toLowerCase();
      const srch = !searchTerm ||
        r.referenceNumber?.toLowerCase().includes(q) ||
        r.role_text?.toLowerCase().includes(q) ||
        r.position?.toLowerCase().includes(q);
      return dMatch && sMatch && srch;
    });
  }, [hiringRequests, deptFilter, statusFilter, searchTerm]);

  // ── Chart Data ────────────────────────────────────────────────────────────

  // 1. Status pie
  const statusPieData = useMemo(() => {
    const counts = hiringRequests.reduce((acc, r) => {
      acc[r.status] = (acc[r.status] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);
  }, [hiringRequests]);

  // 2. Department grouped bar
  const deptBarData = useMemo(() => {
    return departments
      .map(d => ({
        name: d.department_name?.length > 11 ? d.department_name.slice(0, 11) + '…' : d.department_name,
        fullName: d.department_name,
        Requests:   hiringRequests.filter(r => String(r.department_id) === String(d.department_id)).length,
        Candidates: candidates.filter(c => String(c.department_id) === String(d.department_id)).length,
      }))
      .filter(d => d.Requests > 0 || d.Candidates > 0)
      .sort((a, b) => b.Requests - a.Requests)
      .slice(0, 8);
  }, [departments, hiringRequests, candidates]);

  // 3. Monthly trend — last 6 months
  const monthlyTrend = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = d.toLocaleString('default', { month: 'short' });
      months.push({
        month: label,
        Requests: hiringRequests.filter(r => {
          const dc = r.date_created ? new Date(r.date_created) : null;
          return dc && `${dc.getFullYear()}-${String(dc.getMonth()+1).padStart(2,'0')}` === key;
        }).length,
        Candidates: candidates.filter(c => {
          const ad = c.application_date ? new Date(c.application_date) : null;
          return ad && `${ad.getFullYear()}-${String(ad.getMonth()+1).padStart(2,'0')}` === key;
        }).length,
      });
    }
    return months;
  }, [hiringRequests, candidates]);

  // 4. Request type donut
  const typeData = useMemo(() => [
    { name: 'Forecast',    value: hiringRequests.filter(r => r.request_type === 'Forecast').length },
    { name: 'Exceptional', value: hiringRequests.filter(r => r.request_type === 'Exceptional').length },
    { name: 'Outsourced',    value: hiringRequests.filter(r => r.request_type === 'Outsourced').length },
  ].filter(d => d.value > 0), [hiringRequests]);

  // 5. Forecasted hires per dept
  const forecastData = useMemo(() => {
    return departments
      .map(d => ({
        name: d.department_name?.length > 11 ? d.department_name.slice(0, 11) + '…' : d.department_name,
        Forecasted: demandPlans
          .filter(p => String(p.department_id) === String(d.department_id))
          .reduce((s, p) => s + (Number(p.forecasted_hires) || 0), 0),
      }))
      .filter(d => d.Forecasted > 0)
      .sort((a, b) => b.Forecasted - a.Forecasted)
      .slice(0, 8);
  }, [departments, demandPlans]);

  // 6. Priority breakdown
  const priorityData = useMemo(() => {
    const counts = hiringRequests.reduce((acc, r) => {
      if (r.priority) acc[r.priority] = (acc[r.priority] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(counts).map(([name, value]) => ({ name, value }));
  }, [hiringRequests]);

  // 7. Candidate status line (last 6 months)
  const candidateTrend = useMemo(() => {
    const months = [];
    for (let i = 5; i >= 0; i--) {
      const d = new Date();
      d.setMonth(d.getMonth() - i);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({
        month: d.toLocaleString('default', { month: 'short' }),
        Applied:   candidates.filter(c => c.status === 'Applied' && c.application_date?.startsWith(key.slice(0,7))).length,
        Shortlisted: candidates.filter(c => ['ShortListed','Shortlisted For Interview'].includes(c.status) && c.application_date?.startsWith(key.slice(0,7))).length,
        Hired:     candidates.filter(c => c.status === 'Hired' && c.application_date?.startsWith(key.slice(0,7))).length,
      });
    }
    return months;
  }, [candidates]);

  if (loading) return <LoadingSpinner label="Loading Dashboard" />;

  const statusOptions = [
    { value: 'all', label: 'All Statuses' },
    ...['Draft','Submitted','Approved','Rejected','On Hold','Sourcing',
        'Shortlisting','Shortlisted','Interviewing','Hiring Approvals','Offering','Joining','Joined'
    ].map(s => ({ value: s, label: s }))
  ];

  const hasActiveFilters = deptFilter !== 'all' || statusFilter !== 'all' || searchTerm;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="bg-white mx-4 sm:pt-4 sm:px-4 sm:rounded-t-[32px] md:pt-8 md:px-[calc(5%-1rem)] md:rounded-t-[40px] lg:px-[calc(6%-1rem)] xl:h-full xl:pt-24 xl:px-[calc(2.5%-1rem)] xl:rounded-t-[48px] 2xl:px-[calc(8%-1rem)] 3xl:px-[calc(8%-1rem)] 4xl:px-[calc(10%-1rem)]">
      <div className="relative -top-[60px]">

        {/* ── FlipCard KPIs ─────────────────────────────────────────────── */}
        <div className="xl:absolute z-10 w-full grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 mb-8 sm:-top-6 lg:-top-[70px] md:px-[2.5%] lg:px-[2.5%]">
          <FlipCard label="Total Hiring"      count={kpis.total}      imgSrc="/total-request.svg" />
          <FlipCard label="Pending Approval"  count={kpis.pending}    imgSrc="/pending.svg" />
          <FlipCard label="Total Sourcing"    count={kpis.sourcing}   imgSrc="/sourcing.svg" />
          <FlipCard label="Total Candidates"  count={kpis.candidates} imgSrc="/add-employee.svg" />
          <div onClick={() => setShowCreateModal(true)} className="cursor-pointer">
            <div className="w-full bg-center bg-cover p-8 text-center rounded-2xl hover:opacity-90 transition-opacity"
              style={{ backgroundImage: `url('/add-bg.png')` }}>
              <div className="flex flex-col gap-4 items-center">
                <img src="/add.svg" className="w-[40px]" alt="Add" />
                <label className="font-bold text-xl text-white cursor-pointer">Create New Request</label>
              </div>
            </div>
          </div>
        </div>

        {/* ── Main Panel ────────────────────────────────────────────────── */}
        <div className="xl:pt-28">
          <div className="bg-white rounded-2xl overflow-hidden"
            style={{ boxShadow: '0 10px 40px rgba(18,76,114,0.10)', border: '1px solid rgba(18,76,114,0.08)' }}>

            {/* Tab Bar */}
            <div className="flex items-center justify-between px-6 py-4 flex-wrap gap-3"
              style={{ borderBottom: '1px solid rgba(18,76,114,0.08)', background: 'linear-gradient(to right, rgba(18,76,114,0.04), transparent)' }}>
              <div className="flex gap-2 flex-wrap">
                <TabBtn active={activeTab === 'overview'}   onClick={() => setActiveTab('overview')}   count={kpis.pending + kpis.draft}>Overview</TabBtn>
                <TabBtn active={activeTab === 'requests'}   onClick={() => setActiveTab('requests')}   count={filteredRequests.length}>Hiring Requests</TabBtn>
                <TabBtn active={activeTab === 'candidates'} onClick={() => setActiveTab('candidates')} count={kpis.candidates}>Candidates</TabBtn>
                <TabBtn active={activeTab === 'demand'}     onClick={() => setActiveTab('demand')}     count={kpis.demandPlans}>Demand Plans</TabBtn>
              </div>
              <span className="text-xs px-3 py-1 rounded-full font-medium"
                style={{ backgroundColor: 'rgba(18,76,114,0.07)', color: 'rgb(18,76,114)' }}>
                {userRole?.role}
              </span>
            </div>

            {/* ════════════════════════════════════════════════════════════
                OVERVIEW TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'overview' && (
              <div className="p-6 space-y-6">

                {/* ── KPI Grid ───────────────────────────────────────── */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  <KpiCard highlight label="Pending Approval" count={kpis.pending}
                    sub="Awaiting HR review"
                    iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    onClick={() => { setActiveTab('requests'); setStatusFilter('Submitted'); }} />
                  <KpiCard label="Draft" count={kpis.draft}
                    iconPath="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    onClick={() => { setActiveTab('requests'); setStatusFilter('Draft'); }} />
                  <KpiCard label="Approved" count={kpis.approved}
                    iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    onClick={() => { setActiveTab('requests'); setStatusFilter('Approved'); }} />
                  <KpiCard label="On Hold" count={kpis.onHold}
                    iconPath="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    onClick={() => { setActiveTab('requests'); setStatusFilter('On Hold'); }} />
                  <KpiCard label="Rejected" count={kpis.rejected}
                    iconPath="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"
                    onClick={() => { setActiveTab('requests'); setStatusFilter('Rejected'); }} />
                  <KpiCard label="Sourcing" count={kpis.sourcing}
                    iconPath="M17 8l4 4m0 0l-4 4m4-4H3"
                    onClick={() => setActiveTab('requests')} />
                  <KpiCard label="Candidates" count={kpis.candidates}
                    iconPath="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    onClick={() => setActiveTab('candidates')} />
                  <KpiCard label="Demand Plans" count={kpis.demandPlans}
                    iconPath="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
                    onClick={() => setActiveTab('demand')} />
                  <KpiCard label="Forecasted Hires" count={kpis.forecasted}
                    iconPath="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    onClick={() => setActiveTab('demand')} />
                  <KpiCard label="Departments" count={kpis.depts}
                    iconPath="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    onClick={() => {}} />
                </div>

                {/* ── CHARTS ROW 1: Area Trend + Status Pie ──────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Monthly Trend — Area */}
                  <ChartCard colSpan="lg:col-span-2"
                    title="Monthly Activity Trend"
                    subtitle="Hiring requests & candidates over the last 6 months">
                    {monthlyTrend.every(m => m.Requests === 0 && m.Candidates === 0)
                      ? <EmptyChart h={220} />
                      : (
                        <ResponsiveContainer width="100%" height={220}>
                          <AreaChart data={monthlyTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <defs>
                              <linearGradient id="gReq" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="rgb(18,76,114)"   stopOpacity={0.2} />
                                <stop offset="95%" stopColor="rgb(18,76,114)"   stopOpacity={0} />
                              </linearGradient>
                              <linearGradient id="gCand" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%"  stopColor="rgb(16,185,129)"  stopOpacity={0.2} />
                                <stop offset="95%" stopColor="rgb(16,185,129)"  stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,76,114,0.07)" />
                            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" iconSize={8} formatter={legendFormatter} />
                            <Area type="monotone" dataKey="Requests"   stroke="rgb(18,76,114)"  strokeWidth={2.5} fill="url(#gReq)"  dot={{ r: 3, fill: 'rgb(18,76,114)' }} />
                            <Area type="monotone" dataKey="Candidates" stroke="rgb(16,185,129)" strokeWidth={2.5} fill="url(#gCand)" dot={{ r: 3, fill: 'rgb(16,185,129)' }} />
                          </AreaChart>
                        </ResponsiveContainer>
                      )}
                  </ChartCard>

                  {/* Status Donut Pie */}
                  <ChartCard title="Status Distribution" subtitle="Current request statuses">
                    {statusPieData.length === 0
                      ? <EmptyChart h={220} />
                      : (
                        <ResponsiveContainer width="100%" height={220}>
                          <PieChart>
                            <Pie data={statusPieData} cx="50%" cy="45%"
                              innerRadius={50} outerRadius={78}
                              paddingAngle={3} dataKey="value">
                              {statusPieData.map((entry, i) => (
                                <Cell key={i} fill={STATUS_COLORS[entry.name] || CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" iconSize={7} formatter={legendFormatter} />
                          </PieChart>
                        </ResponsiveContainer>
                      )}
                  </ChartCard>
                </div>

                {/* ── CHARTS ROW 2: Dept Grouped Bar + Type Donut ────── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Dept Grouped Bar */}
                  <ChartCard colSpan="lg:col-span-2"
                    title="Requests & Candidates by Department"
                    subtitle="Grouped comparison per department">
                    {deptBarData.length === 0
                      ? <EmptyChart h={220} />
                      : (
                        <ResponsiveContainer width="100%" height={220}>
                          <BarChart data={deptBarData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }} barGap={3}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,76,114,0.07)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" iconSize={7} formatter={legendFormatter} />
                            <Bar dataKey="Requests"   fill="rgb(18,76,114)"        radius={[4,4,0,0]} maxBarSize={22} />
                            <Bar dataKey="Candidates" fill="rgba(16,185,129,0.8)"  radius={[4,4,0,0]} maxBarSize={22} />
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                  </ChartCard>

                  {/* Request Type Donut */}
                  <ChartCard title="Request Type Split" subtitle="Forecast vs Exceptional vs Outsourced">
                    {typeData.length === 0
                      ? <EmptyChart h={170} />
                      : (
                        <>
                          <ResponsiveContainer width="100%" height={160}>
                            <PieChart>
                              <Pie data={typeData} cx="50%" cy="50%"
                                innerRadius={42} outerRadius={65}
                                paddingAngle={4} dataKey="value" startAngle={90} endAngle={-270}>
                                {typeData.map((_, i) => (
                                  <Cell key={i} fill={CHART_COLORS[i]} />
                                ))}
                              </Pie>
                              <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex flex-col gap-1.5 mt-1">
                            {typeData.map((item, i) => (
                              <div key={item.name} className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                  <div className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                                    style={{ backgroundColor: CHART_COLORS[i] }} />
                                  <span className="text-xs" style={{ color: 'rgba(18,76,114,0.7)' }}>{item.name}</span>
                                </div>
                                <span className="text-xs font-bold" style={{ color: 'rgb(18,76,114)' }}>{item.value}</span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                  </ChartCard>
                </div>

                {/* ── CHARTS ROW 3: Forecasted + Priority + Candidate Trend ── */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

                  {/* Forecasted Hires per Dept */}
                  <ChartCard colSpan="lg:col-span-2"
                    title="Forecasted Hires by Department"
                    subtitle={`Demand plan targets for ${new Date().getFullYear()}`}>
                    {forecastData.length === 0
                      ? <EmptyChart h={200} />
                      : (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={forecastData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,76,114,0.07)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="Forecasted" radius={[5,5,0,0]} maxBarSize={36}>
                              {forecastData.map((_, i) => (
                                <Cell key={i} fill={`rgba(18,76,114,${Math.max(0.28, 0.95 - i * 0.09)})`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                  </ChartCard>

                  {/* Priority Donut */}
                  <ChartCard title="Priority Breakdown" subtitle="Request urgency distribution">
                    {priorityData.length === 0
                      ? <EmptyChart h={200} />
                      : (
                        <>
                          <ResponsiveContainer width="100%" height={155}>
                            <PieChart>
                              <Pie data={priorityData} cx="50%" cy="50%"
                                innerRadius={40} outerRadius={62}
                                paddingAngle={4} dataKey="value">
                                {priorityData.map((entry, i) => {
                                  const pc = { High: 'rgba(239,68,68,0.85)', Medium: 'rgba(245,158,11,0.85)', Low: 'rgba(16,185,129,0.85)' };
                                  return <Cell key={i} fill={pc[entry.name] || CHART_COLORS[i]} />;
                                })}
                              </Pie>
                              <Tooltip content={<CustomTooltip />} />
                            </PieChart>
                          </ResponsiveContainer>
                          <div className="flex flex-col gap-1.5 mt-1">
                            {priorityData.map((item) => {
                              const pc = { High: 'rgba(239,68,68,0.85)', Medium: 'rgba(245,158,11,0.85)', Low: 'rgba(16,185,129,0.85)' };
                              return (
                                <div key={item.name} className="flex items-center justify-between">
                                  <div className="flex items-center gap-2">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: pc[item.name] || 'rgb(18,76,114)' }} />
                                    <span className="text-xs" style={{ color: 'rgba(18,76,114,0.7)' }}>{item.name}</span>
                                  </div>
                                  <span className="text-xs font-bold" style={{ color: 'rgb(18,76,114)' }}>{item.value}</span>
                                </div>
                              );
                            })}
                          </div>
                        </>
                      )}
                  </ChartCard>
                </div>

                {/* ── CHARTS ROW 4: Candidate Pipeline Line Chart ─────── */}
                <ChartCard
                  title="Candidate Pipeline Trend"
                  subtitle="Applied → Shortlisted → Hired over last 6 months">
                  {candidateTrend.every(m => m.Applied === 0 && m.Shortlisted === 0 && m.Hired === 0)
                    ? <EmptyChart h={180} />
                    : (
                      <ResponsiveContainer width="100%" height={180}>
                        <LineChart data={candidateTrend} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,76,114,0.07)" />
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                          <Tooltip content={<CustomTooltip />} />
                          <Legend iconType="circle" iconSize={7} formatter={legendFormatter} />
                          <Line type="monotone" dataKey="Applied"     stroke="rgb(18,76,114)"       strokeWidth={2} dot={{ r: 3 }} />
                          <Line type="monotone" dataKey="Shortlisted" stroke="rgba(59,130,246,0.9)" strokeWidth={2} dot={{ r: 3 }} strokeDasharray="5 3" />
                          <Line type="monotone" dataKey="Hired"       stroke="rgba(16,185,129,0.9)" strokeWidth={2} dot={{ r: 3 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                </ChartCard>

                {/* ── Awaiting Action + Notifications ────────────────── */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

                  {/* Awaiting Action */}
                  <div className="rounded-xl p-5"
                    style={{ border: '1px solid rgba(18,76,114,0.1)', backgroundColor: 'rgba(18,76,114,0.02)' }}>
                    <h4 className="font-semibold mb-4 text-sm" style={{ color: 'rgb(18,76,114)' }}>
                      Requests Awaiting Action
                    </h4>
                    {hiringRequests.filter(r => ['Draft','Submitted'].includes(r.status)).length === 0 ? (
                      <p className="text-sm text-center py-8" style={{ color: 'rgba(18,76,114,0.35)' }}>
                        ✓ No pending actions
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                        {hiringRequests
                          .filter(r => ['Draft','Submitted'].includes(r.status))
                          .slice(0, 8)
                          .map(r => {
                            const dept = departments.find(d => String(d.department_id) === String(r.department_id));
                            return (
                              <div key={r.request_id}
                                className="flex items-center justify-between p-3 bg-white rounded-lg cursor-pointer hover:shadow-sm transition-all"
                                style={{ border: '1px solid rgba(18,76,114,0.08)' }}
                                onClick={() => navigate('/request-details', {
                                  state: { request: r, departments, hiringManagers, demandPlans }
                                })}>
                                <div className="min-w-0">
                                  <p className="text-sm font-medium truncate" style={{ color: 'rgb(18,76,114)' }}>
                                    {r.referenceNumber || `REQ-${r.request_id}`}
                                  </p>
                                  <p className="text-xs truncate" style={{ color: 'rgba(18,76,114,0.5)' }}>
                                    {dept?.department_name} · {r.request_type}
                                  </p>
                                </div>
                                <span className="ml-3 px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0"
                                  style={{
                                    backgroundColor: r.status === 'Submitted' ? 'rgba(168,85,247,0.1)' : 'rgba(245,158,11,0.1)',
                                    color: r.status === 'Submitted' ? 'rgb(147,51,234)' : 'rgb(217,119,6)',
                                  }}>
                                  {r.status}
                                </span>
                              </div>
                            );
                          })}
                      </div>
                    )}
                    <button onClick={() => { setActiveTab('requests'); setStatusFilter('Submitted'); }}
                      className="mt-3 text-xs font-medium" style={{ color: 'rgba(18,76,114,0.55)' }}>
                      View all submitted →
                    </button>
                  </div>

                  {/* Notifications */}
                  <div className="rounded-xl p-5"
                    style={{ border: '1px solid rgba(18,76,114,0.1)', backgroundColor: 'rgba(18,76,114,0.02)' }}>
                    <h4 className="font-semibold mb-4 text-sm" style={{ color: 'rgb(18,76,114)' }}>
                      Recent Notifications
                    </h4>
                    {notifications.length === 0 ? (
                      <p className="text-sm text-center py-8" style={{ color: 'rgba(18,76,114,0.35)' }}>
                        No notifications
                      </p>
                    ) : (
                      <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                        {notifications.slice(0, 6).map(n => (
                          <div key={n.notificationID} className="p-3 bg-white rounded-lg"
                            style={{ border: '1px solid rgba(18,76,114,0.08)' }}>
                            <p className="text-xs" style={{ color: 'rgba(18,76,114,0.8)' }}>{n.message}</p>
                            <button onClick={() => navigate('/candidate-details', { state: { id: n.recipientID } })}
                              className="mt-1 text-xs font-medium" style={{ color: 'rgb(18,76,114)' }}>
                              View →
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* ── Status Chip Summary ─────────────────────────────── */}
                <div className="rounded-xl p-5"
                  style={{ border: '1px solid rgba(18,76,114,0.1)' }}>
                  <h4 className="font-semibold mb-4 text-sm" style={{ color: 'rgb(18,76,114)' }}>
                    Request Status Breakdown
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(
                      hiringRequests.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {})
                    ).map(([status, count]) => (
                      <div key={status}
                        onClick={() => { setActiveTab('requests'); setStatusFilter(status); }}
                        className="flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer hover:shadow-sm transition-all"
                        style={{ backgroundColor: 'rgba(18,76,114,0.05)', border: '1px solid rgba(18,76,114,0.1)' }}>
                        <span className="text-sm font-medium" style={{ color: 'rgb(18,76,114)' }}>{status}</span>
                        <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold text-white"
                          style={{ backgroundColor: STATUS_COLORS[status] || 'rgb(18,76,114)' }}>
                          {count}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                HIRING REQUESTS TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'requests' && (
              <div className="p-6">
                {/* Filters */}
                <div className="flex flex-wrap gap-3 mb-6 items-end">
                  <div className="w-48">
                    <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(18,76,114,0.6)' }}>Department</label>
                    <Select
                      options={[{ value: 'all', label: 'All Departments' }, ...departments.map(d => ({ value: d.department_id, label: d.department_name }))]}
                      value={deptFilter === 'all'
                        ? { value: 'all', label: 'All Departments' }
                        : { value: deptFilter, label: departments.find(d => String(d.department_id) === String(deptFilter))?.department_name }}
                      onChange={o => setDeptFilter(o.value)}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="w-44">
                    <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(18,76,114,0.6)' }}>Status</label>
                    <Select
                      options={statusOptions}
                      value={statusOptions.find(o => o.value === statusFilter)}
                      onChange={o => setStatusFilter(o.value)}
                      styles={selectStyles}
                    />
                  </div>
                  <div className="flex-1 min-w-48">
                    <label className="block text-xs font-medium mb-1" style={{ color: 'rgba(18,76,114,0.6)' }}>Search</label>
                    <div className="relative">
                      <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
                        placeholder="Reference, role, position..."
                        className="w-full px-4 py-2 pl-9 rounded-xl text-sm outline-none"
                        style={{ border: '1px solid rgba(18,76,114,0.25)', color: 'rgb(18,76,114)' }}
                        onFocus={e => e.target.style.boxShadow = '0 0 0 3px rgba(18,76,114,0.12)'}
                        onBlur={e => e.target.style.boxShadow = 'none'} />
                      <svg className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'rgba(18,76,114,0.4)' }}
                        fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                  </div>
                  {hasActiveFilters && (
                    <button onClick={() => { setDeptFilter('all'); setStatusFilter('all'); setSearchTerm(''); }}
                      className="px-3 py-2 rounded-xl text-sm font-medium"
                      style={{ backgroundColor: 'rgba(239,68,68,0.07)', color: '#dc2626', border: '1px solid rgba(239,68,68,0.2)' }}>
                      Clear
                    </button>
                  )}
                  <span className="text-sm px-3 py-2 rounded-xl"
                    style={{ backgroundColor: 'rgba(18,76,114,0.05)', color: 'rgb(18,76,114)', border: '1px solid rgba(18,76,114,0.1)' }}>
                    <strong>{filteredRequests.length}</strong> of <strong>{hiringRequests.length}</strong>
                  </span>
                </div>

                {filteredRequests.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 gap-3">
                    <div className="w-14 h-14 rounded-full flex items-center justify-center"
                      style={{ backgroundColor: 'rgba(18,76,114,0.06)' }}>
                      <svg className="w-7 h-7" style={{ color: 'rgba(18,76,114,0.3)' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"
                          d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    </div>
                    <p className="font-semibold" style={{ color: 'rgb(18,76,114)' }}>No requests found</p>
                    <p className="text-sm" style={{ color: 'rgba(18,76,114,0.5)' }}>Try adjusting your filters</p>
                  </div>
                ) : (
                  <HiringRequestCards
                    filteredHiringRequests={filteredRequests}
                    demandPlans={demandPlans}
                    hiringManagers={hiringManagers}
                    visibleCount={visibleCount}
                    departments={departments}
                  />
                )}
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                CANDIDATES TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'candidates' && (
              <div className="p-4">
                <CandidateCardsList
                  candidates={candidates}
                  departments={departments}
                  demandPlans={demandPlans}
                  isEmbedded={true}
                />
              </div>
            )}

            {/* ════════════════════════════════════════════════════════════
                DEMAND PLANS TAB
            ════════════════════════════════════════════════════════════ */}
            {activeTab === 'demand' && (
              <div className="p-6 space-y-6">

                {/* Demand Plan Charts */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <ChartCard title="Forecasted Hires by Department" subtitle={`${new Date().getFullYear()} targets`}>
                    {forecastData.length === 0
                      ? <EmptyChart h={200} />
                      : (
                        <ResponsiveContainer width="100%" height={200}>
                          <BarChart data={forecastData} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(18,76,114,0.07)" vertical={false} />
                            <XAxis dataKey="name" tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'rgba(18,76,114,0.55)' }} axisLine={false} tickLine={false} />
                            <Tooltip content={<CustomTooltip />} />
                            <Bar dataKey="Forecasted" radius={[5,5,0,0]} maxBarSize={36}>
                              {forecastData.map((_, i) => (
                                <Cell key={i} fill={`rgba(18,76,114,${Math.max(0.28, 0.95 - i * 0.09)})`} />
                              ))}
                            </Bar>
                          </BarChart>
                        </ResponsiveContainer>
                      )}
                  </ChartCard>

                  <ChartCard title="Plans per Department" subtitle="Distribution of demand plans">
                    {(() => {
                      const plansByDept = departments
                        .map(d => ({
                          name: d.department_name?.length > 14 ? d.department_name.slice(0, 14) + '…' : d.department_name,
                          Plans: demandPlans.filter(p => String(p.department_id) === String(d.department_id)).length,
                        }))
                        .filter(d => d.Plans > 0);
                      return plansByDept.length === 0 ? <EmptyChart h={200} /> : (
                        <ResponsiveContainer width="100%" height={200}>
                          <PieChart>
                            <Pie data={plansByDept} dataKey="Plans" cx="50%" cy="50%"
                              outerRadius={75} paddingAngle={3}>
                              {plansByDept.map((_, i) => (
                                <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip />} />
                            <Legend iconType="circle" iconSize={7} formatter={legendFormatter} />
                          </PieChart>
                        </ResponsiveContainer>
                      );
                    })()}
                  </ChartCard>
                </div>

                {/* Demand Plan Table */}
                <div className="overflow-x-auto rounded-xl" style={{ border: '1px solid rgba(18,76,114,0.1)' }}>
                  <table className="min-w-full">
                    <thead style={{ backgroundColor: 'rgba(18,76,114,0.05)' }}>
                      <tr>
                        {['Reference','Department','Role','Grade','Forecasted Hires','Year'].map(h => (
                          <th key={h} className="py-3 px-4 text-left text-sm font-semibold"
                            style={{ color: 'rgb(18,76,114)', borderBottom: '1px solid rgba(18,76,114,0.1)' }}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {demandPlans.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="py-12 text-center text-sm"
                            style={{ color: 'rgba(18,76,114,0.4)' }}>
                            No demand plans found
                          </td>
                        </tr>
                      ) : demandPlans.map((plan, idx) => {
                        const dept = departments.find(d => String(d.department_id) === String(plan.department_id));
                        return (
                          <tr key={plan.demand_id} className="transition-colors hover:bg-blue-50"
                            style={{ backgroundColor: idx % 2 === 0 ? 'white' : 'rgba(18,76,114,0.015)' }}>
                            {[
                              plan.referenceNumber || 'N/A',
                              dept?.department_name || 'N/A',
                              plan.role || 'N/A',
                              plan.grade || 'N/A',
                              plan.forecasted_hires || 0,
                              plan.yearOfPlan || 'N/A',
                            ].map((val, i) => (
                              <td key={i} className="py-3 px-4 text-sm"
                                style={{ borderTop: '1px solid rgba(18,76,114,0.06)', color: 'rgba(18,76,114,0.85)' }}>
                                {val}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end">
                  <button onClick={() => navigate('/demand-plan')}
                    className="px-5 py-2.5 rounded-xl text-sm font-medium text-white transition-all"
                    style={{ backgroundColor: 'rgb(18,76,114)', boxShadow: '0 4px 12px rgba(18,76,114,0.25)' }}
                    onMouseEnter={e => e.currentTarget.style.opacity = '0.88'}
                    onMouseLeave={e => e.currentTarget.style.opacity = '1'}>
                    Manage Demand Plans →
                  </button>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      <CreateHiringRequestModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={() => window.location.reload()}
      />
    </div>
  );
};

export default AdminDashboard;